import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import type { AppMode } from "@/types/convex";

/**
 * Hook to manage level app mode (whitelist, blacklist, categories).
 * 
 * Key principle: Mode changes ONLY update the appMode field - they never modify
 * appFilter, allowedApps, or blockedApps. This allows settings to persist
 * when switching between modes.
 * 
 * Sidebar visibility by mode:
 * - whitelist: Settings, Whitelist only
 * - blacklist: Settings, Blacklist only
 * - categories: All sections (Settings, Categories, Whitelist, Blacklist, All Apps)
 */
export function useLevelAppMode(levelId: string) {
  const { toast } = useToast();
  
  // Get level data - we need to find the level from the list
  const levelsData = useQuery(api.levels.list.all, {
    paginationOpts: { cursor: null, numItems: 100 }
  });
  
  // Mutation for updating level apps - sends appMode directly
  const updateLevelApps = useMutation(api.levels.apps.update);
  
  // Find the current level
  const currentLevel = levelsData?.page?.find(level => level._id === levelId);
  
  /**
   * Determine current app mode.
   * Priority: 
   * 1. Explicit appMode from level
   * 2. Inferred from appFilter (fallback for backwards compatibility)
   */
  const getAppMode = (): AppMode => {
    // Read explicit appMode from level if available
    const explicitMode = (currentLevel as any)?.appMode;
    if (explicitMode) return explicitMode;
    
    // Fallback: infer from appFilter for backwards compatibility
    const appFilter = currentLevel?.appFilter;
    
    // appFilter === true means Apple App Store (blacklist mode)
    if (typeof appFilter === 'boolean' || (appFilter as any) === true) {
      return "blacklist";
    }
    
    // Check for whitelist-only marker
    if (typeof appFilter === 'object' && appFilter !== null) {
      const hasWhitelistMarker = "whitelist-only" in appFilter;
      if (hasWhitelistMarker) {
        return "whitelist";
      }
    }
    
    // Default to categories mode
    return "categories";
  };
  
  const appMode = getAppMode();
  
  /**
   * Set the app mode.
   * 
   * Sends appMode directly to backend - does NOT modify appFilter, allowedApps, or blockedApps.
   * This allows settings to persist when switching between modes.
   */
  const setAppMode = async (mode: AppMode) => {
    if (!levelId) {
      toast({
        title: "Error",
        description: "Level ID not available",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Send appMode directly to backend
      // Cast to any since api.ts types may not include appMode yet
      await updateLevelApps({
        level: levelId,
        appMode: mode,
      } as any);
      
      toast({
        title: "App Mode Updated",
        description: `Level app mode set to ${mode}.`,
      });
    } catch (error) {
      console.error("Error updating app mode:", error);
      toast({
        title: "Error",
        description: "Failed to update app mode",
        variant: "destructive",
      });
    }
  };
  
  return {
    appMode,
    setAppMode,
    isLoading: levelsData === undefined,
  };
}
